import React, { useEffect } from 'react';
import Select from 'react-select';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { ProjectDataForm, Product, ProjectUserOption } from '../../../../app/models/Project';
import { CostCenterDataForm } from '../../../../app/models/CostCenter';

type Props = {
  projectDataForm: ProjectDataForm;
  setProjectDataForm: (data: ProjectDataForm) => void;
  products: Product[];
  users: ProjectUserOption[];
  costCenters: CostCenterDataForm[];
  disabled?: boolean;
  errorFields?: any;
};

const ProjectForm = ({
  projectDataForm,
  setProjectDataForm,
  products,
  users = [],
  costCenters = [],
  disabled = false,
  errorFields
}: Props) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

  useEffect(() => {
    setErrors(errorFields);
  }, [errorFields, setErrors]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setProjectDataForm({ ...projectDataForm, [name]: value });
  };

  const handleChangeSelectTwo = (name: string, value: number | string | null | undefined) => {
    setProjectDataForm({ ...projectDataForm, [name]: value ?? null });
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const v = value.trim();
    if (v === '') {
      setProjectDataForm({ ...projectDataForm, [name]: null } as any);
      return;
    }
    const normalized = v.replace(',', '.');
    const num = Number(normalized);
    setProjectDataForm({ ...projectDataForm, [name]: isNaN(num) ? null : num } as any);
  };

  // Convertir productos a formato de react-select
  const productOptions = products.map(product => ({
    value: product.id,
    label: product.name
  }));

  // Obtener las opciones seleccionadas
  const selectedProducts = productOptions.filter(option =>
    projectDataForm.planted_variety_ids?.includes(option.value)
  );

  const handleProductsChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
    setProjectDataForm({
      ...projectDataForm,
      planted_variety_ids: selectedIds
    });
  };

  const userOptions = users.map(user => ({
    value: user.id,
    label: user.name
  }));
  const selectedUsers = userOptions.filter(option =>
    projectDataForm.user_ids?.includes(option.value)
  );
  const handleUsersChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
    setProjectDataForm({
      ...projectDataForm,
      user_ids: selectedIds
    });
  };

  return (
    <>
      {/* Datos básicos */}
      <div className="row g-2 mb-2">
        <div className="col-12">
          <label className="col-form-label">Nombre <span className="text-danger">*</span></label>
          <input
            name="name"
            id="name"
            type="text"
            className={`form-control ${fieldHasError('name')}`}
            value={projectDataForm.name ?? ''}
            onChange={handleChange}
            onFocus={() => onFocusRemove('name')}
            placeholder="Ingrese el nombre del proyecto"
            disabled={disabled}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('name')}
          </div>
        </div>
      </div>

      <div className="row g-2 mb-2">
        <div className="col-12">
          <label className="col-form-label">Descripción</label>
          <textarea
            name="description"
            id="description"
            className={`form-control ${fieldHasError('description')}`}
            value={projectDataForm.description ?? ''}
            onChange={handleChange}
            onFocus={() => onFocusRemove('description')}
            placeholder="Describa el proyecto"
            rows={3}
            disabled={disabled}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('description')}
          </div>
        </div>
      </div>

      {/* Centro de costo / Superficie / Variedades */}
      <div className="row g-2 mb-2">
        <div className="col-md-6">
          <label className="col-form-label">Centro de costo <span className="text-danger">*</span></label>
          <SelectTwo
            name="cost_center_id"
            id="cost_center_id"
            inputValue={projectDataForm.cost_center_id ?? null}
            options={SelectTwoMapperOptions(costCenters, 'id', ['name'])}
            hasError={fieldHasError('cost_center_id') !== ''}
            onChange={(value: any) => handleChangeSelectTwo('cost_center_id', value?.value)}
            placeholder="Seleccione centro de costo"
            onFocus={() => onFocusRemove('cost_center_id')}
            isClearable={false}
            disabled={disabled}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('cost_center_id')}
          </div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Superficie</label>
          <div className="input-group">
            <input
              name="surface"
              id="surface"
              type="number"
              step="0.01"
              className={`form-control ${fieldHasError('surface')}`}
              value={projectDataForm.surface ?? ''}
              onChange={handleNumberChange}
              onFocus={() => onFocusRemove('surface')}
              placeholder="Ej: 1200.50"
              disabled={disabled}
            />
            <span className="input-group-text">m²</span>
          </div>
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('surface')}
          </div>
        </div>
      </div>

            {/* Áreas */}
      <div className="row g-2 mb-2">
        <div className="col-md-4">
          <label className="col-form-label">Área total</label>
          <div className="input-group">
            <input
              name="total_area"
              id="total_area"
              type="number"
              step="0.01"
              className={`form-control ${fieldHasError('total_area')}`}
              value={projectDataForm.total_area ?? ''}
              onChange={handleNumberChange}
              onFocus={() => onFocusRemove('total_area')}
              placeholder="Ej: 1500.00"
              disabled={disabled}
            />
            <span className="input-group-text">m²</span>
          </div>
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('total_area')}
          </div>
        </div>

        <div className="col-md-4">
          <label className="col-form-label">Área ocupada</label>
          <div className="input-group">
            <input
              name="occupied_area"
              id="occupied_area"
              type="number"
              step="0.01"
              className={`form-control ${fieldHasError('occupied_area')}`}
              value={projectDataForm.occupied_area ?? ''}
              onChange={handleNumberChange}
              onFocus={() => onFocusRemove('occupied_area')}
              placeholder="Ej: 1100.00"
              disabled={disabled}
            />
            <span className="input-group-text">m²</span>
          </div>
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('occupied_area')}
          </div>
        </div>

        <div className="col-md-4">
          <label className="col-form-label">Área ociosa</label>
          <div className="input-group">
            <input
              name="idle_area"
              id="idle_area"
              type="number"
              step="0.01"
              className={`form-control ${fieldHasError('idle_area')}`}
              value={projectDataForm.idle_area ?? ''}
              onChange={handleNumberChange}
              onFocus={() => onFocusRemove('idle_area')}
              placeholder="Ej: 400.00"
              disabled={disabled}
            />
            <span className="input-group-text">m²</span>
          </div>
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('idle_area')}
          </div>
        </div>
      </div>

      {/* Cantidades / Invernaderos / Trabajadores / Pasillos */}
      <div className="row g-2 mb-2">
        <div className="col-md-3">
          <label className="col-form-label">Cant. plantada</label>
          <input
            name="planted_quantity"
            id="planted_quantity"
            type="number"
            className={`form-control ${fieldHasError('planted_quantity')}`}
            value={projectDataForm.planted_quantity ?? ''}
            onChange={handleNumberChange}
            onFocus={() => onFocusRemove('planted_quantity')}
            placeholder="Ej: 15000"
            disabled={disabled}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('planted_quantity')}
          </div>
        </div>

        <div className="col-md-3">
          <label className="col-form-label"># Naves</label>
          <input
            name="greenhouses_count"
            id="greenhouses_count"
            type="number"
            className={`form-control ${fieldHasError('greenhouses_count')}`}
            value={projectDataForm.greenhouses_count ?? ''}
            onChange={handleNumberChange}
            onFocus={() => onFocusRemove('greenhouses_count')}
            placeholder="Ej: 6"
            disabled={disabled}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('greenhouses_count')}
          </div>
        </div>

        <div className="col-md-3">
          <label className="col-form-label"># Tuneles</label>
          <input
            name="corridors_count"
            id="corridors_count"
            type="number"
            className={`form-control ${fieldHasError('corridors_count')}`}
            value={projectDataForm.corridors_count ?? ''}
            onChange={handleNumberChange}
            onFocus={() => onFocusRemove('corridors_count')}
            placeholder="Ej: 12"
            disabled={disabled}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('corridors_count')}
          </div>
        </div>

        <div className="col-md-3">
          <label className="col-form-label"># Trabajadores</label>
          <input
            name="workers_count"
            id="workers_count"
            type="number"
            className={`form-control ${fieldHasError('workers_count')}`}
            value={projectDataForm.workers_count ?? ''}
            onChange={handleNumberChange}
            onFocus={() => onFocusRemove('workers_count')}
            placeholder="Ej: 18"
            disabled={disabled}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('workers_count')}
          </div>
        </div>

      </div>

      {/* Variedades plantadas */}
      <div className="row g-2 mb-2">
        <div className="col-md-12">
          <label className="col-form-label">Variedades plantadas</label>
          <Select
            isMulti
            name="planted_variety_ids"
            options={productOptions}
            value={selectedProducts}
            onChange={handleProductsChange}
            onFocus={() => onFocusRemove('planted_variety_ids')}
            isDisabled={disabled}
            placeholder="Seleccione variedades..."
            noOptionsMessage={() => 'No hay productos disponibles'}
            className={fieldHasError('planted_variety_ids') ? 'is-invalid' : ''}
            classNamePrefix="select"
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: fieldHasError('planted_variety_ids') ? '#dc3545' : base.borderColor,
                '&:hover': {
                  borderColor: fieldHasError('planted_variety_ids') ? '#dc3545' : base.borderColor
                }
              })
            }}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('planted_variety_ids')}
          </div>
        </div>
      </div>

      {/* Trabajadores del proyecto */}
      <div className="row g-2 mb-2">
        <div className="col-md-12">
          <label className="col-form-label">Trabajadores</label>
          <Select
            isMulti
            name="user_ids"
            options={userOptions}
            value={selectedUsers}
            onChange={handleUsersChange}
            onFocus={() => onFocusRemove('user_ids')}
            isDisabled={disabled}
            placeholder="Seleccione trabajadores..."
            noOptionsMessage={() => 'No hay trabajadores disponibles'}
            className={fieldHasError('user_ids') ? 'is-invalid' : ''}
            classNamePrefix="select"
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: fieldHasError('user_ids') ? '#dc3545' : base.borderColor,
                '&:hover': {
                  borderColor: fieldHasError('user_ids') ? '#dc3545' : base.borderColor
                }
              })
            }}
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('user_ids')}
          </div>
        </div>
      </div>



      {/* Económico (solo lectura: actualizados por sistema) */}
      <div className="row g-2 mb-0">
        <div className="col-md-6">
          <label className="col-form-label">Costos (CLP)</label>
          <input
            name="costs"
            id="costs"
            type="number"
            step="0.01"
            className={`form-control bg-light ${fieldHasError('costs')}`}
            value={projectDataForm.costs ?? ''}
            readOnly
            placeholder="0"
            disabled
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('costs')}
          </div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Ventas (CLP)</label>
          <input
            name="sales"
            id="sales"
            type="number"
            step="0.01"
            className={`form-control bg-light ${fieldHasError('sales')}`}
            value={projectDataForm.sales ?? ''}
            readOnly
            placeholder="0"
            disabled
          />
          <div className="invalid-feedback d-flex">
            {fieldErrorMessage('sales')}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectForm;